<template>
  <div class="px-6 py-4">
    <div class="flex items-center justify-between mb-4">
      <button type="button" class="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors" @click="$emit('back')">
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-xs" />
        Back to funnel
      </button>
      <div class="flex items-center gap-2">
        <button v-if="data && data.brief" type="button" class="btn-secondary-sm" @click="$emit('open-brief', { practice_key: data.practice.practice_key })">Read the brief</button>
        <button type="button" class="btn-primary-sm" :disabled="running" @click="run">{{ running ? 'Reading engagements…' : (intel ? 'Refresh intelligence' : 'Run intelligence') }}</button>
      </div>
    </div>

    <LoadingIndicator v-if="loading" message="Loading pursuit..." />
    <div v-else-if="!data" class="text-sm text-gray-500 py-8 text-center">Nothing here.</div>
    <template v-else>
      <div class="bg-white rounded-lg border border-gray-200">
        <div class="p-5 border-b border-gray-200">
          <div class="flex justify-between items-start gap-4">
            <div>
              <h2 class="sensitive text-xl font-semibold text-gray-900">{{ data.practice.name }}</h2>
              <div class="text-sm text-gray-600 mt-0.5">
                <span class="sensitive">{{ [data.practice.city, data.practice.state].filter(Boolean).join(', ') }}</span>
                <span v-if="data.practice.owner_name"> · Owner <span class="sensitive">{{ data.practice.owner_name }}</span></span>
                <span v-if="data.practice.status"> · Salesforce: <span class="sensitive">{{ data.practice.status }}</span></span>
              </div>
            </div>
            <div class="text-right text-xs text-gray-500 leading-5 shrink-0">
              <span v-if="data.intelligence_at">Intelligence {{ fmtWhen(data.intelligence_at) }}</span>
              <span v-else>No intelligence yet</span>
              <br /><a v-if="data.practice.salesforce_url" :href="data.practice.salesforce_url" target="_blank" rel="noopener" class="text-neutral-700 underline decoration-dotted">Open in Salesforce</a>
            </div>
          </div>
          <div class="flex flex-wrap gap-1.5 mt-3">
            <PracticeTag v-if="intel" category="type" :variant="intel.momentum === 'warming' ? 'success' : intel.momentum === 'cooling' ? 'warning' : undefined">{{ momentumLabel }}</PracticeTag>
            <PracticeTag v-if="intel" category="aco">{{ stageLabel }}</PracticeTag>
            <PracticeTag v-if="data.brief && data.brief.verdict" category="setting">{{ verdictLabel }}</PracticeTag>
            <PracticeTag v-for="t in briefTags" :key="t" category="tech">{{ t }}</PracticeTag>
          </div>
          <p v-if="intel" class="sensitive text-sm text-gray-800 mt-3" data-demo-blur>{{ intel.summary }}</p>
          <p v-else-if="data.brief" class="sensitive text-sm text-gray-700 mt-3" data-demo-blur>
            <span class="font-semibold text-gray-900">From the brief.</span> {{ data.brief.headline }}
          </p>
        </div>

        <div v-if="intel" class="p-5 grid grid-cols-[1fr_380px] gap-8 items-start">
          <div class="space-y-7">
            <section>
              <h3 class="heading-subsection mb-2">Who we are talking to</h3>
              <div class="text-sm text-gray-800 space-y-1">
                <div><span class="text-gray-500">Champion</span> <span class="sensitive" data-demo-blur>{{ intel.relationship.champion || 'Not identified yet' }}</span></div>
                <div><span class="text-gray-500">Decision maker</span> <span class="sensitive" data-demo-blur>{{ intel.relationship.decision_maker || 'Not identified yet' }}</span></div>
              </div>
              <ul v-if="intel.relationship.people_met.length" class="mt-2 divide-y divide-gray-100 border-y border-gray-100">
                <li v-for="(p, i) in intel.relationship.people_met" :key="i" class="py-2 text-sm">
                  <span class="font-medium text-gray-900 sensitive">{{ p.name }}</span><span class="text-gray-500"> · {{ p.role }}</span>
                  <div class="sensitive text-gray-700" data-demo-blur>{{ p.stance }} <span class="text-[11px] text-gray-400">{{ cite(p.engagement_ids) }}</span></div>
                </li>
              </ul>
            </section>

            <section>
              <h3 class="heading-subsection mb-2">What landed</h3>
              <ul v-if="intel.what_landed.length" class="divide-y divide-gray-100 border-y border-gray-100">
                <li v-for="(w, i) in intel.what_landed" :key="i" class="flex gap-3 py-2.5 text-sm">
                  <font-awesome-icon :icon="['fas', 'check']" class="mt-1 text-xs shrink-0 text-secondary-800" />
                  <span class="sensitive" data-demo-blur><span class="font-medium text-gray-900">{{ w.point }}</span> <span class="text-gray-700">{{ w.evidence }}</span> <span class="text-[11px] text-gray-400">{{ cite(w.engagement_ids) }}</span></span>
                </li>
              </ul>
              <p v-else class="text-sm text-gray-500">Nothing has clearly landed yet.</p>
            </section>

            <section>
              <h3 class="heading-subsection mb-2">Objections</h3>
              <ul v-if="intel.objections.length" class="divide-y divide-gray-100 border-y border-gray-100">
                <li v-for="(o, i) in intel.objections" :key="i" class="py-2.5 text-sm">
                  <div class="flex items-center gap-2">
                    <span class="inline-block w-2 h-2 rounded-sm" :class="o.status === 'addressed' ? 'bg-secondary-800' : o.status === 'open' ? 'bg-yellow-dark' : 'bg-gray-300'"></span>
                    <span class="font-medium text-gray-900 sensitive" data-demo-blur>{{ o.objection }}</span>
                    <span class="text-[11px] text-gray-500">{{ o.status }}</span>
                    <span class="text-[11px] text-gray-400">{{ cite(o.engagement_ids) }}</span>
                  </div>
                  <div class="sensitive text-gray-700 mt-1 pl-4" data-demo-blur>{{ o.our_answer }}</div>
                </li>
              </ul>
              <p v-else class="text-sm text-gray-500">No objections raised so far.</p>
            </section>

            <section>
              <h3 class="heading-subsection mb-2">Commitments</h3>
              <table v-if="intel.commitments.length" class="w-full text-sm">
                <thead>
                  <tr class="text-xs text-gray-500 border-b border-gray-200">
                    <th class="text-left font-medium py-2 pr-3 w-32">Who</th>
                    <th class="text-left font-medium py-2 px-3">What</th>
                    <th class="text-left font-medium py-2 px-3 w-28">Due</th>
                    <th class="text-left font-medium py-2 pl-3 w-24">Status</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="(c, i) in intel.commitments" :key="i">
                    <td class="py-2 pr-3 sensitive"><span class="text-gray-900">{{ c.who }}</span><span class="text-[11px] text-gray-500"> · {{ c.side === 'us' ? 'us' : 'practice' }}</span></td>
                    <td class="py-2 px-3 text-gray-800 sensitive" data-demo-blur>{{ c.what }} <span class="text-[11px] text-gray-400">{{ cite(c.engagement_ids) }}</span></td>
                    <td class="py-2 px-3 text-gray-700">{{ c.due }}</td>
                    <td class="py-2 pl-3" :class="c.status === 'overdue' ? 'text-red-dark font-medium' : 'text-gray-700'">{{ c.status }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="text-sm text-gray-500">No commitments on record.</p>
            </section>

            <section v-if="intel.open_questions.length">
              <h3 class="heading-subsection mb-2">Still need to find out</h3>
              <ul class="list-disc pl-5 text-sm text-gray-800 space-y-1">
                <li v-for="(q, i) in intel.open_questions" :key="i" class="sensitive" data-demo-blur>{{ q }}</li>
              </ul>
            </section>
          </div>

          <aside class="rounded-lg border border-gray-900 p-4 sticky top-4">
            <h3 class="heading-subsection mb-1">Next meeting</h3>
            <div v-if="nextMeeting" class="text-xs text-gray-500 mb-3">{{ fmtDate(nextMeeting.date) }} · <span class="sensitive">{{ nextMeeting.subject }}</span></div>
            <p class="sensitive text-sm font-medium text-gray-900 mb-3" data-demo-blur>{{ intel.next_meeting.objective }}</p>
            <ol class="list-decimal pl-5 space-y-2 text-sm">
              <li v-for="(a, i) in intel.next_meeting.agenda" :key="i" class="sensitive" data-demo-blur>
                <span class="text-gray-900">{{ a.item }}</span>
                <div class="text-[12px] text-gray-500">{{ a.why }}</div>
              </li>
            </ol>
            <div v-if="intel.next_meeting.asks.length" class="mt-4">
              <div class="text-xs font-semibold text-gray-500 mb-1">Asks</div>
              <ul class="list-disc pl-5 text-sm text-gray-800 space-y-1">
                <li v-for="(a, i) in intel.next_meeting.asks" :key="i" class="sensitive" data-demo-blur>{{ a }}</li>
              </ul>
            </div>
            <div v-if="intel.next_meeting.risks.length" class="mt-4">
              <div class="text-xs font-semibold text-gray-500 mb-1">Watch for</div>
              <ul class="list-disc pl-5 text-sm text-gray-800 space-y-1">
                <li v-for="(r, i) in intel.next_meeting.risks" :key="i" class="sensitive" data-demo-blur>{{ r }}</li>
              </ul>
            </div>
          </aside>
        </div>
        <div v-else class="p-5 text-sm text-gray-600">
          {{ data.engagements.length }} engagement{{ data.engagements.length === 1 ? '' : 's' }} on record and no intelligence yet. Run it to get the summary and the next-meeting agenda.
        </div>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 mt-4">
        <div class="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 class="heading-subsection">Engagements</h3>
          <span class="text-xs text-gray-500">{{ data.engagements.length }} from Salesforce</span>
        </div>
        <div v-if="!data.engagements.length" class="px-5 py-8 text-sm text-gray-500">No engagements yet.</div>
        <div v-else class="divide-y divide-gray-100">
          <div v-for="e in data.engagements" :key="e.id" class="px-5 py-3">
            <button type="button" class="w-full text-left flex items-start gap-4" @click="toggle(e.id)">
              <div class="w-20 shrink-0 text-xs text-gray-500 pt-0.5">{{ fmtDate(e.date) }}</div>
              <div class="flex-1 min-w-0">
                <div class="text-sm">
                  <span class="font-medium text-gray-900 sensitive">{{ e.subject || '(no subject)' }}</span>
                  <span class="text-gray-500"> · {{ e.channel }}</span>
                  <span v-if="e.status === 'SCHEDULED'" class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700">Scheduled</span>
                  <span class="text-[11px] text-gray-400 ml-2">[{{ e.id }}]</span>
                </div>
                <div v-if="!open.has(e.id)" class="sensitive text-sm text-gray-600 truncate" data-demo-blur>{{ e.note }}</div>
              </div>
              <div class="text-xs text-gray-400 shrink-0">{{ e.transcript ? 'transcript' : '' }}</div>
            </button>
            <div v-if="open.has(e.id)" class="ml-24 mt-2 space-y-3">
              <div v-if="e.note" class="sensitive text-sm text-gray-800 whitespace-pre-line" data-demo-blur>{{ e.note }}</div>
              <div v-if="e.transcript" class="rounded-md bg-gray-50 border border-gray-100 p-3">
                <div class="text-xs font-semibold text-gray-500 mb-1">Transcript</div>
                <pre class="sensitive text-[12px] leading-5 text-gray-800 whitespace-pre-wrap font-sans" data-demo-blur>{{ e.transcript }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import PracticeTag from './PracticeTag.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import { STAGE_LABEL, MOMENTUM_LABEL } from './PursuitsTab.vue'

const VERDICT_LABEL = { fixable: 'Fixable', partly: 'Partly fixable', stuck: 'Stuck' }

export default {
  name: 'PursuitTab',
  components: { FontAwesomeIcon, PracticeTag, LoadingIndicator },
  props: {
    entityId: { type: Number, default: null },
    tagLabels: { type: Object, default: () => ({}) },
  },
  emits: ['back', 'open-brief'],
  data() {
    return { data: null, loading: true, running: false, open: new Set() }
  },
  computed: {
    intel() {
      return this.data && this.data.intelligence
    },
    momentumLabel() {
      return MOMENTUM_LABEL[this.intel.momentum] || this.intel.momentum
    },
    stageLabel() {
      return STAGE_LABEL[this.intel.stage_read] || this.intel.stage_read
    },
    verdictLabel() {
      return VERDICT_LABEL[this.data.brief.verdict] || this.data.brief.verdict
    },
    briefTags() {
      return ((this.data.brief && this.data.brief.tags) || []).map((t) => this.tagLabels[t] || t)
    },
    nextMeeting() {
      const future = (this.data.engagements || []).filter((e) => e.status === 'SCHEDULED')
      return future.length ? future[future.length - 1] : null
    },
  },
  watch: {
    entityId() {
      this.load()
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      if (!this.entityId) return
      this.loading = true
      try {
        const { data } = await this.$axios.get(`/api/growth/pursuits/${this.entityId}/`)
        this.data = data && data.practice ? data : null
      } finally {
        this.loading = false
      }
    },
    async run() {
      this.running = true
      try {
        await this.$axios.post(`/api/growth/pursuits/${this.entityId}/`)
        await this.load()
      } finally {
        this.running = false
      }
    },
    toggle(id) {
      const next = new Set(this.open)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      this.open = next
    },
    cite(ids) {
      return ids && ids.length ? '[' + ids.join(', ') + ']' : ''
    },
    fmtDate(iso) {
      if (!iso) return ''
      return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    fmtWhen(iso) {
      const d = new Date(iso)
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>
